import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Tabs, Tab, Table, TableBody, TableCell, 
  TableContainer, TableHead, TableRow, Chip, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

interface Partnership {
  _id: string;
  requestType: 'sponsor' | 'organizer';
  name: string;
  email: string;
  contactNumber?: string;
  description: string;
  sponsorType?: string;
  collegeOrSociety?: string;
  reach?: string;
  pocPosition?: string;
  status: 'pending' | 'contacted' | 'resolved';
  createdAt: string;
}

const PartnershipsPage: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);
  const [partnerships, setPartnerships] = useState<Partnership[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<Partnership | null>(null);
  const { user } = useAuth();

  const fetchPartnerships = async () => {
    try {
      const response = await axios.get(`${API_URL}/partnerships`, {
        withCredentials: true
      });
      setPartnerships(response.data);
    } catch (error) {
      console.error('Error fetching partnerships:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPartnerships();
  }, []);

  const handleStatusUpdate = async (id: string, newStatus: string) => {
    try {
      await axios.put(`${API_URL}/partnerships/${id}`, { status: newStatus }, {
        withCredentials: true
      });
      setPartnerships(partnerships.map(p => p._id === id ? { ...p, status: newStatus as any } : p));
      setSelectedRequest(null);
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);
  };

  const filteredData = partnerships.filter(p => p.requestType === (tabIndex === 0 ? 'sponsor' : 'organizer'));

  return (
    <Box>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', color: '#1a237e' }}>
        Partnerships & Sponsorships
      </Typography>

      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabIndex} onChange={handleTabChange} indicatorColor="primary" textColor="primary">
          <Tab label="Sponsors & Incubators" />
          <Tab label="Organizers" />
        </Tabs>
      </Paper>

      <TableContainer component={Paper} elevation={2}>
        <Table>
          <TableHead sx={{ backgroundColor: '#f5f7fa' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Name / POC</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
              {tabIndex === 0 ? (
                <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
              ) : (
                <TableCell sx={{ fontWeight: 'bold' }}>College/Society</TableCell>
              )}
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">Loading...</TableCell>
              </TableRow>
            ) : filteredData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">No requests found.</TableCell>
              </TableRow>
            ) : (
              filteredData.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell>{new Date(row.createdAt).toLocaleDateString()}</TableCell>
                  <TableCell>{row.name}</TableCell>
                  <TableCell>{row.email}</TableCell>
                  
                  {tabIndex === 0 ? (
                    <TableCell sx={{ textTransform: 'capitalize' }}>{row.sponsorType}</TableCell>
                  ) : (
                    <TableCell>{row.collegeOrSociety}</TableCell>
                  )}

                  <TableCell>
                    <Chip 
                      label={row.status.toUpperCase()} 
                      size="small"
                      color={
                        row.status === 'pending' ? 'warning' :
                        row.status === 'contacted' ? 'info' : 'success'
                      } 
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="View Details">
                      <IconButton color="primary" onClick={() => setSelectedRequest(row)}>
                        <InfoIcon />
                      </IconButton>
                    </Tooltip>
                    {row.status !== 'resolved' && (
                      <Tooltip title="Mark as Resolved">
                        <IconButton color="success" onClick={() => handleStatusUpdate(row._id, 'resolved')}>
                          <CheckCircleIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Details Dialog */}
      <Dialog open={!!selectedRequest} onClose={() => setSelectedRequest(null)} maxWidth="md" fullWidth>
        {selectedRequest && (
          <>
            <DialogTitle sx={{ backgroundColor: '#f5f7fa', pb: 2 }}>
              Request Details - {selectedRequest.name}
            </DialogTitle>
            <DialogContent dividers>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={3}>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Email</Typography>
                  <Typography variant="body1">{selectedRequest.email}</Typography>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="textSecondary">Contact Number</Typography>
                  <Typography variant="body1">{selectedRequest.contactNumber || 'N/A'}</Typography>
                </Box>
                
                {selectedRequest.requestType === 'sponsor' ? (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary">Sponsor Type</Typography>
                    <Typography variant="body1" sx={{ textTransform: 'capitalize' }}>{selectedRequest.sponsorType}</Typography>
                  </Box>
                ) : (
                  <>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">College / Society</Typography>
                      <Typography variant="body1">{selectedRequest.collegeOrSociety}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Position</Typography>
                      <Typography variant="body1">{selectedRequest.pocPosition || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="textSecondary">Expected Reach</Typography>
                      <Typography variant="body1">{selectedRequest.reach || 'N/A'}</Typography>
                    </Box>
                  </>
                )}
              </Box>

              <Typography variant="subtitle2" color="textSecondary">Description / Message</Typography>
              <Paper sx={{ p: 2, bgcolor: '#f8f9fa', mt: 1 }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedRequest.description}
                </Typography>
              </Paper>
            </DialogContent>
            <DialogActions>
              {selectedRequest.status === 'pending' && (
                <Button color="primary" onClick={() => handleStatusUpdate(selectedRequest._id, 'contacted')}>
                  Mark as Contacted
                </Button>
              )}
              {selectedRequest.status !== 'resolved' && (
                <Button color="success" onClick={() => handleStatusUpdate(selectedRequest._id, 'resolved')}>
                  Mark as Resolved
                </Button>
              )}
              <Button onClick={() => setSelectedRequest(null)} color="inherit">Close</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default PartnershipsPage;
